import { Structure, StructureClaim } from '../types';

export type ReadinessLevel = 'none' | 'risk' | 'partial' | 'progress';

/**
 * User-facing word for each level. Lives here rather than in ReadinessMeter
 * so the meter and the map key can't drift apart — the key exists to explain
 * the same four colors the meter names.
 */
export const READINESS_LABELS: Record<ReadinessLevel, string> = {
  progress: 'On track',
  partial: 'In progress',
  risk: 'Work needed',
  none: 'Not started',
};

/**
 * Derive a readiness level from a structure's denormalized claimSummary.
 * Used by map markers which only have the structure doc (not the claim).
 */
export function getReadinessLevel(structure: Structure): ReadinessLevel {
  const pct = structure.claimSummary?.progressPercent;
  if (pct == null || pct === 0) return 'none';
  return getReadinessLevelFromPercent(pct);
}

/**
 * Derive readiness from a claim's full progress snapshot.
 * Used by detail screens that have the claim doc.
 */
export function getReadinessLevelFromClaim(claim: StructureClaim | null): ReadinessLevel {
  const snap = claim?.progressSnapshot;
  if (!snap || snap.total <= 0) return 'none';
  return getReadinessLevelFromCounts(snap.completed, snap.total);
}

export function getReadinessLevelFromCounts(completed: number, total: number): ReadinessLevel {
  if (total <= 0) return 'none';
  if (completed <= 0) return 'none';
  const pct = completed / total;
  if (pct >= 0.66) return 'progress';
  if (pct >= 0.33) return 'partial';
  return 'risk';
}

export function getReadinessLevelFromPercent(pct: number): ReadinessLevel {
  if (pct >= 66) return 'progress';
  if (pct >= 33) return 'partial';
  return pct > 0 ? 'risk' : 'none';
}

export function getReadinessPercent(claim: StructureClaim | null): number | null {
  const snap = claim?.progressSnapshot;
  if (!snap || snap.total <= 0) return null;
  return Math.round((snap.completed / snap.total) * 100);
}

/**
 * Map a ReadinessLevel to the appropriate theme color token.
 * Returns `colors.neutral` for `'none'` if provided, else `null` so callers
 * with no neutral can pick their own fallback (e.g. map markers).
 */
export function getReadinessColor(
  readiness: ReadinessLevel,
  colors: { progress: string; partial: string; risk: string; neutral?: string }
): string | null {
  switch (readiness) {
    case 'progress':
      return colors.progress;
    case 'partial':
      return colors.partial;
    case 'risk':
      return colors.risk;
    default:
      return colors.neutral ?? null;
  }
}
